import { useEffect, useState } from 'react';

import { useDisconnectWhatsApp } from './use-onboarding';

import type { ChannelConfigWire } from './types';
import type { ReactElement } from 'react';

const PARA = 'pl-3.5 before:mr-2 before:inline-block before:h-1.5 before:w-1.5 before:rounded-full';
const PERDE = `${PARA} before:bg-red-700`;
const FICA = `${PARA} before:bg-emerald-700`;

/**
 * B4 — desconectar ou trocar de número. A consequência é dita ANTES do clique: verde é o que
 * fica, vermelho é o que para. Nenhum dos dois apaga conversa. Trocar de número = desconectar
 * e, em seguida, «Conectar WhatsApp» de novo — ao desconectar, a lista de canais é invalidada,
 * o cartão «Não conectado» reaparece com o botão, e o caminho é o mesmo da primeira conexão.
 *
 * A confirmação é digitar o número mostrado. Canal pré-W1 não tem `displayPhoneNumber`
 * gravado: nesse caso pede-se o nome de exibição.
 */
export function DisconnectDialog({
  config,
  modo,
  onClose,
  onDone,
}: {
  config: ChannelConfigWire;
  modo: 'desconectar' | 'trocar';
  onClose: () => void;
  onDone: () => void;
}): ReactElement {
  const numero = config.config.displayPhoneNumber;
  const alvo = (typeof numero === 'string' && numero !== '' ? numero : config.displayName) ?? '';
  const pedeNome = alvo === config.displayName;
  const [digitado, setDigitado] = useState('');
  const disconnect = useDisconnectWhatsApp();
  const normal = (s: string): string => s.replace(/[\s-]/g, '').toLowerCase();
  const confere = alvo !== '' && normal(digitado) === normal(alvo);

  // Escape fecha (revisão de 30/Set, menor 8) — menos no meio do desconectar.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape' && !disconnect.isPending) onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose, disconnect.isPending]);

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="disc-title"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-xl bg-surface shadow-lg">
        <div className="px-6 pt-5">
          <h2 id="disc-title" className="text-base font-semibold text-text-primary">
            {modo === 'trocar' ? 'Trocar de número' : 'Desconectar o WhatsApp da Lume?'}
          </h2>
          <p className="mt-0.5 text-xs text-text-secondary">
            {modo === 'trocar'
              ? 'O número atual sai; o novo entra pelo mesmo caminho de conexão.'
              : alvo}
          </p>
        </div>
        <ul className="grid gap-1.5 px-6 py-4 text-xs leading-relaxed text-text-secondary">
          <li className={PERDE}>
            {modo === 'trocar' ? (
              <>
                O <strong>{alvo}</strong> deixa de atender pela Lume.
              </>
            ) : (
              <>
                Os clientes <strong>deixam de chegar aqui</strong> a partir de agora. Quem escrever
                não recebe resposta pela Lume.
              </>
            )}
          </li>
          {modo === 'desconectar' && (
            <li className={PERDE}>Campanhas e lembretes agendados por este número são pausados.</li>
          )}
          <li className={FICA}>
            O número <strong>continua seu</strong> na Meta. Nada muda lá; a Lume só deixa de ter
            acesso.
          </li>
          <li className={FICA}>
            As conversas e os clientes ficam guardados aqui, pelo prazo da sua política de
            privacidade.
          </li>
          {modo === 'desconectar' && (
            <li className={FICA}>
              Para voltar, é só «Conectar WhatsApp» de novo — a Meta pode pedir a confirmação do
              número outra vez.
            </li>
          )}
          {modo === 'trocar' && (
            <li className={FICA}>
              Os modelos de mensagem aprovados ficam na sua conta da Meta e voltam a aparecer se o
              novo número estiver na mesma conta.
            </li>
          )}
        </ul>
        <div className="px-6 pb-4">
          <label className="block text-[11px] font-medium text-text-secondary" htmlFor="confirma">
            Para confirmar, digite o número{pedeNome ? ' (o nome de exibição)' : ''}:{' '}
            <span className="font-mono">{alvo}</span>
          </label>
          <input
            id="confirma"
            value={digitado}
            onChange={(e) => {
              setDigitado(e.target.value);
            }}
            className="mt-1 w-full rounded-md border border-border-strong bg-surface px-3 py-2 font-mono text-xs text-text-primary"
            autoComplete="off"
          />
          {disconnect.isError && (
            <p className="mt-2 text-xs text-red-700" role="alert">
              Não foi possível desconectar: {disconnect.error.message}
            </p>
          )}
        </div>
        <div className="flex justify-end gap-2 border-t border-border-default bg-bg-subtle px-6 py-3.5">
          <button
            type="button"
            className="rounded-md border border-border-strong bg-surface px-3 py-1.5 text-xs text-text-secondary"
            onClick={onClose}
          >
            Cancelar
          </button>
          <button
            type="button"
            disabled={!confere || disconnect.isPending}
            className={`rounded-md px-3 py-1.5 text-xs font-medium disabled:opacity-50 ${
              modo === 'trocar'
                ? 'bg-accent text-white'
                : 'border border-red-200 bg-red-50 text-red-700'
            }`}
            onClick={() => {
              disconnect.mutate(undefined, { onSuccess: onDone });
            }}
          >
            {disconnect.isPending
              ? 'Desconectando…'
              : modo === 'trocar'
                ? 'Continuar para conectar o novo'
                : 'Desconectar o WhatsApp'}
          </button>
        </div>
      </div>
    </div>
  );
}
